/**
 * `fetch`, with a per-attempt timeout and bounded retries on network failure or a 5xx.
 *
 * Built for `completeSurveySession`: a respondent who has just finished a survey
 * is one blip on that single POST away from being stranded — finished in their own eyes, never
 * marked (and never paid) as complete. A 4xx is never retried: it means the request itself was
 * rejected (bad session, already complete), and retrying it would just repeat the same rejection.
 */

export class CompletionTimeoutError extends Error {
  constructor() {
    super('Request timed out');
    this.name = 'CompletionTimeoutError';
  }
}

export interface ResilientFetchOptions {
  timeoutMs: number;
  retries: number;
  backoffMs: number;
  fetchImpl?: typeof fetch;
}

/** Anything at or above this status is treated as a transient server failure and retried. */
const RETRYABLE_STATUS_FLOOR = 500;

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export async function fetchWithTimeoutAndRetry(
  url: string,
  init: RequestInit,
  opts: ResilientFetchOptions,
): Promise<Response> {
  const doFetch = opts.fetchImpl ?? fetch;
  let lastError: unknown = new CompletionTimeoutError();

  for (let attempt = 0; attempt <= opts.retries; attempt++) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), opts.timeoutMs);
    try {
      const res = await doFetch(url, { ...init, signal: controller.signal });
      if (res.status < RETRYABLE_STATUS_FLOOR) return res;
      lastError = new Error(`HTTP ${res.status}`);
    } catch (err) {
      lastError =
        err instanceof DOMException && err.name === 'AbortError'
          ? new CompletionTimeoutError()
          : err;
    } finally {
      clearTimeout(timer);
    }
    if (attempt < opts.retries) await sleep(opts.backoffMs * 2 ** attempt);
  }

  throw lastError;
}
